import React, { Children, useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { CHEVRON_DOWN_V3, CHEVRON_UP_V3 } from '../../../constants/images';
import { TRANSLATIONS_GLOBAL } from '../../../translations/commons';
import BdsButton from '../Buttons/BdsButton/BdsButton';

import './ExpansiveContainer.scss';

interface IExpansiveContainerProps {
  initialRows?: number;
}

const ExpansiveContainer = ({ children, initialRows = 2 }: React.PropsWithChildren<IExpansiveContainerProps>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [cardsPerRow, setCardsPerRow] = useState(1);
  const { t } = useTranslation();

  const childrenArray = Children.toArray(children);
  const totalChildren = childrenArray.length;

  useEffect(() => {
    const calculateCardsPerRow = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const cardWidth = 356;
        const gap = 40;
        const effectiveWidth = cardWidth + gap;
        const count = Math.floor((containerWidth + gap) / effectiveWidth);
        setCardsPerRow(Math.max(count, 1));
      }
    };

    const observer = new ResizeObserver(() => calculateCardsPerRow());
    if (containerRef.current) observer.observe(containerRef.current);

    calculateCardsPerRow();

    return () => observer.disconnect();
  }, []);

  const visibleCount = cardsPerRow * initialRows;
  const needsExpansion = totalChildren > visibleCount;

  const toggleExpand = useCallback(() => {
    setExpanded((prev) => !prev);
    if (!expanded) {
      setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [expanded]);

  const getButtonText = useCallback(() => {
    return expanded ? t(TRANSLATIONS_GLOBAL.seeLess) : t(TRANSLATIONS_GLOBAL.seeMore);
  }, [expanded, t]);

  const visibleChildren = needsExpansion && !expanded ? childrenArray.slice(0, visibleCount) : childrenArray;

  return (
    <div className="expansive-container flex flex-col gap-8" ref={containerRef}>
      <div className={'cards-grid gap-10'}>{visibleChildren}</div>

      {needsExpansion && (
        <div className="toggle-button-container">
          <BdsButton
            action={toggleExpand}
            variant="inverse"
            size="s"
            iconRight={expanded ? CHEVRON_UP_V3 : CHEVRON_DOWN_V3}
          >
            {getButtonText()}
          </BdsButton>
        </div>
      )}
    </div>
  );
};

export default ExpansiveContainer;
